/**
 * Utility functions for handling real-time report updates via WebSocket
 */

/**
 * Merges updated report data into an existing report, preserving unchanged fields
 *
 * @param {object} existingReport - The current report object
 * @param {object} updatedData - The partial update data from WebSocket notification
 * @returns {object} - New report object with updated fields merged in
 */
export const mergeReportUpdate = (existingReport, updatedData) => {
  if (!existingReport || typeof existingReport !== 'object') {
    throw new Error('Invalid existing report: must be an object');
  }

  if (!updatedData || typeof updatedData !== 'object') {
    throw new Error('Invalid update data: must be an object');
  }

  // Merge updated fields while preserving unchanged fields
  return {
    ...existingReport,
    ...updatedData
  };
};

/**
 * Updates a specific report in a report list based on ID
 *
 * @param {Array} reports - The current list of reports
 * @param {object} reportUpdate - The report update data (must contain an 'id' field)
 * @returns {Array} - New array with the updated report, or original array if report not found
 */
export const handleReportUpdate = (reports, reportUpdate) => {
  // Validate inputs
  if (!Array.isArray(reports)) {
    throw new Error('Invalid reports: must be an array');
  }

  if (!reportUpdate || typeof reportUpdate !== 'object') {
    throw new Error('Invalid report update: must be an object');
  }

  // Handle malformed notification data - missing ID
  if (!reportUpdate.id && reportUpdate.id !== 0) {
    console.warn('Report update missing ID field, ignoring update:', reportUpdate);
    return reports;
  }

  // Find the report by ID
  const reportIndex = reports.findIndex(r => r.id === reportUpdate.id);

  // Gracefully handle reports not in current list
  if (reportIndex === -1) {
    // Report not in current page/list, ignore silently
    return reports;
  }

  // Create new array with updated report
  const updatedReports = [...reports];
  updatedReports[reportIndex] = mergeReportUpdate(reports[reportIndex], reportUpdate);

  return updatedReports;
};

/**
 * Creates a state updater function for use with React's setState
 * This ensures we work with the latest state and handle race conditions properly
 *
 * @param {object} reportUpdate - The report update data
 * @returns {function} - State updater function that takes previous state and returns new state
 */
export const createReportUpdateHandler = (reportUpdate) => {
  return (prevReports) => {
    try {
      return handleReportUpdate(prevReports, reportUpdate);
    } catch (error) {
      console.error('Error handling report update:', error, reportUpdate);
      // Return previous state unchanged on error
      return prevReports;
    }
  };
};
